import React, { useEffect, useRef } from 'react';
import { XCircle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Button from '../ui/Button';
import PhoneInput from '../ui/PhoneInput';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent, { DISCLAIMER_LINK_CLASS_NAME } from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

type ContactInfoFormProps = {
  formData: FormData;
  validationErrors: {
    email: string | null;
    phone: string | null;
  };
  loading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
};

export default function ContactInfoForm({
  formData,
  validationErrors,
  loading,
  error,
  onChange,
  onSubmit,
}: ContactInfoFormProps) {
  const { t } = useTranslation('wizard');
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  useEffect(() => {
    // Focus the phone input when the component mounts
    phoneInputRef.current?.focus();
  }, []);

  const handlePhoneChange = (value: string) => {
    onChange({
      target: {
        name: 'phone',
        value
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const isFormValid = () => {
    return (
      formData.phone &&
      formData.smsConsent === true &&
      !validationErrors.phone
    );
  };

  return (
    <form id="contact-info-form" name="contact-info-form" onSubmit={onSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="contact-info-title"
          name="contact-info-title"
          as="h2" 
          className="mb-2"
        >
          {t('contactInfo.title')}
        </Title>
      </div>

      <div className="space-y-4">
        <PhoneInput
          ref={phoneInputRef}
          id="contact-info-phone"
          name="contact-info-phone"
          value={formData.phone || ''}
          onChange={handlePhoneChange}
          error={validationErrors.phone}
          label={t('contactInfo.phoneLabel')}
          required
        />
        
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <Button
        id="contact-info-submit"
        name="contact-info-submit"
        type="submit"
        disabled={loading || !isFormValid()}
        loading={loading}
        loadingText={t('contactInfo.processing')}
        fullWidth
        size="lg"
      >
        {t('contactInfo.submit')}
      </Button>

      {showDisclaimer && (
        <Disclaimer
          id="contact-info-general-disclaimer"
          name="contact-info-general-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}

      <Disclaimer
        id="contact-info-disclaimer"
        name="contact-info-disclaimer"
      >
        <p className="mb-4">
          <Trans
            i18nKey="contactInfo.smsDisclaimer"
            ns="wizard"
            components={{
              partnersLink: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </p>

        <p>
          <Trans
            i18nKey="contactInfo.creditDisclaimer"
            ns="wizard"
            components={{
              partnersLink: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              spinwheelLink: (
                <a
                  href="https://legal.spinwheel.io/end-user-agreement/"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </p>
      </Disclaimer>
    </form>
  );
}